import { orderBy, sortBy } from 'lodash';

interface PrimalWithCuts {
  _id: string;
  name: string;
  order: number;
  groupOrder: number;
  groupName: string;
  cuts: {
    name: string;
    isSelected: boolean;
    order: number;
  }[];
  notes?: string;
}

/**
 * Builds template data for cutsheet preview from capability form values
 * @param values
 * @param capability
 * @param hideUnselectedCuts
 */
export function buildCutsheetPreviewData(
  values: any,
  capability: any,
  hideUnselectedCuts = false,
) {
  // Sample notes for random assignment
  const sampleNotes = [
    'Cut thick for steaks',
    'Customer prefers bone-in',
    'Extra trimming requested',
    'Keep fat cap on',
    'Cut into 1" portions',
    'Butterfly if possible',
    'Remove silver skin',
  ];

  console.log(values);

  // Build all primals from primalGroups
  const allPrimals: PrimalWithCuts[] = [];

  if (values.primalGroups) {
    values.primalGroups.forEach((group: any) => {
      if (group.isDeleted) return;

      group.primals?.forEach((primal: any) => {
        if (primal.isDeleted) return;

        let cuts = (primal.cuts || [])
          .filter((cut: any) => !cut.isDeleted)
          .map((cut: any) => ({
            name: cut.printedText || cut.name,
            isSelected: Math.random() > 0.5, // Randomly select ~50% of cuts
            order: cut.order || 0,
          }));

        // Filter out unselected cuts if hideUnselectedCuts is true
        if (hideUnselectedCuts) {
          cuts = cuts.filter((cut: { isSelected: boolean }) => cut.isSelected);
        }

        // Skip primals with no cuts
        if (cuts.length === 0) return;

        // Randomly add notes to ~30% of primals
        const shouldHaveNotes = Math.random() > 0.7;
        const randomNote = shouldHaveNotes
          ? sampleNotes[Math.floor(Math.random() * sampleNotes.length)]
          : undefined;

        allPrimals.push({
          _id: primal._id,
          name: primal.name,
          order: primal.order || 0,
          groupOrder: group.order || 0,
          groupName: group.name || '',
          cuts: sortBy(cuts, 'order'),
          notes: randomNote || primal.notes,
        });
      });
    });
  }

  // Sort primals by group order then primal order
  const sortedPrimals = orderBy(
    allPrimals,
    ['groupOrder', 'order'],
    ['asc', 'asc'],
  );

  // Build specifications
  const specifications = (values.specifications || []).map((spec: any) => ({
    key: spec.name,
    value: spec.value || '',
    notes: spec.notes,
  }));

  // Build trim
  const trim = (values.trim || []).map((item: any, index: number) => ({
    name: item.name,
    quantity: item.weightInLb,
    rank: index + 1,
  }));

  // Build extras
  const extras = (values.extras || []).map((item: any) => ({
    name: item.name,
    quantity: item.weightInLb,
  }));

  return {
    animal_split_portion: `${capability?.animalSpecies?.toUpperCase() || 'ANIMAL'} - WHOLE`,
    cutsheet_name: 'Preview Cutsheet',
    cutsheet_notes: 'This is a preview of the cutsheet design.',
    layout_specs_in_sidebar: true,
    base_font_size: 12,
    primal_columns: 2,
    always_show_primal_notes: true,
    use_cross: false,
    contact: {
      name: 'Customer Name',
      phone: '(555) 123-4567',
      email: 'customer@example.com',
    },
    dropoff_date: new Date().toLocaleDateString(),
    kill_date: new Date().toLocaleDateString(),
    hanging_weight: '450 lbs',
    tracking_number: 'TRACK-123',
    producer_identifier: 'PRODUCER-456',
    order_number: 'ORDER-789',
    requested_by: {
      name: 'Producer Name',
      phone: '(555) 987-6543',
      email: 'producer@example.com',
    },
    vendor: {
      logo_url:
        'http://cdn.mcauto-images-production.sendgrid.net/41874974eecec83f/bda00e30-6df5-4af6-8810-436b764d03ac/250x250.png',
      name: capability?.vendor?.name || 'Processor Name',
    },
    all_primals: sortedPrimals,
    cutsheet_information: specifications,
    trim,
    extras,
  };
}
